<!-- CsvExportDialog.vue -->
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 z-50"
  >
    <div
      class="glass-card dark:text-gray-50 w-full max-w-4xl my-8 relative flex flex-col"
    >
      <!-- Header -->
      <div class="sticky top-0 glass-card z-20 border-b dark:border-gray-700">
        <div class="flex justify-between items-center p-4">
          <h2 class="text-xl font-bold">Export CSV</h2>
          <button @click="onClose" class="btn hover:text-red-500">
            <span class="text-3xl">&times;</span>
          </button>
        </div>

        <!-- Select All -->
        <div class="px-4 pb-4">
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="select-all"
              :checked="allSelected"
              @change="toggleAll"
              class="h-4 w-4 rounded border-gray-300"
            />
            <label for="select-all" class="font-medium"
              >Select All Fields</label
            >
          </div>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <div class="space-y-6">
          <div
            v-for="(columns, category) in groupedColumns"
            :key="category"
            class="space-y-2"
          >
            <h3 class="font-medium text-gray-700 dark:text-gray-50">{{ category }}</h3>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div
                v-for="column in columns"
                :key="column.key"
                class="flex items-center space-x-2"
              >
                <input
                  type="checkbox"
                  :id="column.key"
                  v-model="selectedColumns"
                  :value="column.key"
                  class="h-4 w-4 rounded border-gray-300"
                />
                <label :for="column.key" class="text-sm">{{
                  column.label
                }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="sticky bottom-0 glass-card z-20 border-t dark:border-gray-700 p-4">
        <div
          v-if="exportError"
          class="mb-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-3 rounded relative"
          role="alert"
        >
          <div class="flex items-center justify-between">
            <span class="block sm:inline text-sm">{{ exportError }}</span>
            <button
              @click="exportError = null"
              class="text-red-500 hover:text-red-700 dark:text-red-300 dark:hover:text-red-100"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        <div class="flex justify-end space-x-2">
          <button
            @click="onClose"
            class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            @click="exportCsv"
            :disabled="!selectedColumns.length"
            :class="[
              'btn',
              selectedColumns.length
              ? 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 '
              : 'bg-gray-300 dark:bg-gray-300 dark:text-gray-50 text-gray-500 cursor-not-allowed',
            ]"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { getSitesWithParams } from "@/services/siteService";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { extractErrorMessage } from "@/utils/errorMessages";
import { useStore } from "@/store";

const CsvExportDialog = {
  name: "CsvExportDialog",
  props: {
    isOpen: Boolean,
    data: {
      type: Array,
      required: true,
    },
  },
  emits: ["close"],

  setup(props, { emit }) {
    const tenant = useMultiTenant();
    const selectedColumns = ref([]);
    const exportError = ref(null);
    const allColumns = [
      { key: "name", label: "Name", category: "Basic Information" },
      { key: "job_name_override", label: "Job Name Override", category: "Basic Information" },
      { key: "site_name_override", label: "Site Name Override", category: "Basic Information" },
      { key: "device_name", label: "Device Name", category: "Basic Information" },
      { key: "legacy_name", label: "Legacy Name", category: "Basic Information" },
      { key: "org", label: "Organisation", category: "Basic Information" },
      { key: "in_maintenance", label: "In Maintenance", category: "Basic Information" },
      { key: "tag", label: "Tag", category: "Basic Information" },
      { key: "mgmt_loopback_address", label: "Management IP Address", category: "Network Configuration" },
      { key: "mgmt_loopback_address_port", label: "Management IP Address Port", category: "Network Configuration" },
      { key: "api_key", label: "API Key", category: "Network Configuration" },
      { key: "api_port", label: "API Port", category: "Network Configuration" },
      { key: "api_address", label: "API Address", category: "Network Configuration" },
      { key: "code", label: "Code", category: "Location Details" },
      { key: "description", label: "Description", category: "Location Details" },
      { key: "description_abbreviate", label: "Description Abbreviation", category: "Location Details" },
      { key: "country", label: "Country", category: "Location Details" },
      { key: "region", label: "Region", category: "Location Details" },
      { key: "division", label: "Division", category: "Location Details" },
      { key: "approvedsite", label: "Approved Site", category: "Status" },
      { key: "pending", label: "Pending", category: "Status" },
      { key: "scrape_interval", label: "Scrape Interval", category: "Monitoring", staffOnly: true },
      { key: "scrape_timeout", label: "Scrape Timeout", category: "Monitoring", staffOnly: true },
      { key: "cordinates_latitude", label: "Latitude", category: "Geographic Data" },
      { key: "cordinates_longitude", label: "Longitude", category: "Geographic Data" },
      { key: "geohash", label: "Geohash", category: "Geographic Data" },
      { key: "dashboard_link", label: "Dashboard Link", category: "Other" },
      { key: "snmp_targets", label: "SNMP Targets", category: "Other" },
      { key: "created_date", label: "Created Date", category: "Other" },
      { key: "last_modified", label: "Last Modified", category: "Other" },
    ];

    const availableColumns = computed(() =>
      allColumns.filter(c => !c.staffOnly || useStore.isStaff())
    );

    const allSelected = computed(() =>
      selectedColumns.value.length === availableColumns.value.length
    );

    const groupedColumns = computed(() => {
      const groups = {};
      availableColumns.value.forEach(col => {
        if (!groups[col.category]) groups[col.category] = [];
        groups[col.category].push(col);
      });
      return groups;
    });

    const toggleAll = () => {
      if (allSelected.value) {
        selectedColumns.value = [];
      } else {
        selectedColumns.value = availableColumns.value.map((col) => col.key);
      }
    };

    const onClose = () => {
      selectedColumns.value = [];
      emit("close");
    };

    const exportCsv = async () => {
      exportError.value = null;
      try {
        const exportData = await getSitesWithParams({
          fields: selectedColumns.value.join(","),
          ...tenant.addSchemaParam(),
        });

        // Generate CSV
        const headers = availableColumns.value
          .filter((col) => selectedColumns.value.includes(col.key))
          .map((col) => col.label)
          .join(",");

        const rows = exportData.map((item) =>
          selectedColumns.value
            .map((key) => {
              const value = item[key];
              // Handle special cases
              if (Array.isArray(value)) return `"${value.join("; ")}"`;
              if (typeof value === "boolean") return value ? "Yes" : "No";
              if (value === null || value === undefined) return "";
              return `"${value.toString().replace(/"/g, '""')}"`;
            })
            .join(",")
        );

        const csvContent = [headers, ...rows].join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "sites-export.csv");
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
        onClose();
      } catch (error) {
        console.error("Error exporting CSV:", error);
        exportError.value = extractErrorMessage(error, "Failed to export CSV. Please try again.");
      }
    };

    return {
      selectedColumns,
      availableColumns,
      groupedColumns,
      allSelected,
      exportError,
      toggleAll,
      onClose,
      exportCsv,
    };
  },
};

export default CsvExportDialog;
</script>
